import { useState } from 'react';

interface HouseholdMember {
  id: string;
  name: string;
  relationship: string;
  dateOfBirth: string;
  ssn: string;
  coveredPolicies: string[];
}

export default function HouseholdMembers() {
  const [showAddForm, setShowAddForm] = useState(false);
  const [members, setMembers] = useState<HouseholdMember[]>([
    {
      id: '1',
      name: 'Jane Smith',
      relationship: 'Spouse',
      dateOfBirth: '1987-03-22',
      ssn: '***-**-5678',
      coveredPolicies: ['Auto Insurance', 'Home Insurance', 'Life Insurance']
    },
    {
      id: '2',
      name: 'Michael Smith',
      relationship: 'Child',
      dateOfBirth: '2010-09-15',
      ssn: '***-**-9012',
      coveredPolicies: ['Auto Insurance', 'Home Insurance']
    },
    {
      id: '3',
      name: 'Emily Smith',
      relationship: 'Child',
      dateOfBirth: '2013-12-08',
      ssn: '***-**-3456',
      coveredPolicies: ['Home Insurance']
    }
  ]);

  const [newMember, setNewMember] = useState({
    name: '',
    relationship: '',
    dateOfBirth: '',
    ssn: '',
    coveredPolicies: []
  });

  const handleAddMember = () => {
    const member: HouseholdMember = {
      id: Date.now().toString(),
      ...newMember,
      coveredPolicies: []
    };
    setMembers([...members, member]);
    setNewMember({ name: '', relationship: '', dateOfBirth: '', ssn: '', coveredPolicies: [] });
    setShowAddForm(false);
  };

  const removeMember = (id: string) => {
    setMembers(members.filter(member => member.id !== id));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Household Members</h1>
        <button
          onClick={() => setShowAddForm(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-add-line mr-2"></i>
          Add Member
        </button>
      </div>

      {/* Primary Policyholder */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Primary Policyholder</h2>
        </div>
        <div className="p-6">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
              <i className="ri-user-star-line text-2xl text-blue-600"></i>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900">John Smith (You)</h3>
              <p className="text-sm text-gray-500">Primary Policyholder • Born June 15, 1985</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  Auto Insurance
                </span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Home Insurance
                </span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  Life Insurance
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Household Members */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Additional Household Members</h2>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            {members.map((member) => (
              <div key={member.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                    <i className="ri-user-line text-xl text-gray-600"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{member.name}</h3>
                    <p className="text-sm text-gray-500">
                      {member.relationship} • Born {new Date(member.dateOfBirth).toLocaleDateString()}
                    </p>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {member.coveredPolicies.map((policy, index) => (
                        <span key={index} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {policy}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button className="p-2 text-gray-400 hover:text-blue-600 cursor-pointer">
                    <i className="ri-edit-line"></i>
                  </button>
                  <button
                    onClick={() => removeMember(member.id)}
                    className="p-2 text-gray-400 hover:text-red-600 cursor-pointer"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Add Member Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Add Household Member</h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                <input
                  type="text"
                  value={newMember.name}
                  onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="Enter full name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Relationship</label>
                <select
                  value={newMember.relationship}
                  onChange={(e) => setNewMember({ ...newMember, relationship: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm pr-8"
                >
                  <option value="">Select relationship</option>
                  <option value="Spouse">Spouse</option>
                  <option value="Child">Child</option>
                  <option value="Parent">Parent</option>
                  <option value="Sibling">Sibling</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth</label>
                <input
                  type="date"
                  value={newMember.dateOfBirth}
                  onChange={(e) => setNewMember({ ...newMember, dateOfBirth: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Social Security Number</label>
                <input
                  type="text"
                  value={newMember.ssn}
                  onChange={(e) => setNewMember({ ...newMember, ssn: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="XXX-XX-XXXX"
                />
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowAddForm(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={handleAddMember}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                Add Member
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}